import React, { useState, useEffect } from 'react';
import useAuthenticatedAxios from "../auth/AuthenticatedAxios";
import { useAuth0 } from '@auth0/auth0-react';
import styled, { keyframes } from 'styled-components';
import { useToast } from '../Context/ToastContext';
import { useConfirm } from '../Context/ConfirmContext';

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

// Styled Components for Modern Design
const PageWrapper = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #0a0d12 0%, #111820 50%, #0d1117 100%);
  padding: 40px;
  box-sizing: border-box;
  overflow-y: auto;
  overflow-x: hidden;
`;

const Container = styled.div`
  width: 100%;
  max-width: 900px;
  color: #FFFFFF;
  padding-bottom: 60px;
  animation: ${fadeIn} 0.4s ease-out;
`;

const Header = styled.div`
  margin-bottom: 40px;
  text-align: center;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 600;
  margin-bottom: 12px;
  color: #FFFFFF;
  letter-spacing: -0.5px;
  background: linear-gradient(135deg, #fff 0%, #94a3b8 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: rgba(148, 163, 184, 0.8);
  line-height: 1.6;
  max-width: 600px;
  margin: 0 auto;
`;

const InfoBox = styled.div`
  background: rgba(59, 130, 246, 0.08);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 32px;
  display: flex;
  align-items: center;
  gap: 14px;
  backdrop-filter: blur(10px);
`;

const InfoIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #FFF;
  font-weight: 600;
  font-size: 14px;
  flex-shrink: 0;
`;

const InfoText = styled.span`
  font-size: 13px;
  color: rgba(203, 213, 225, 0.9);
  line-height: 1.5;
  
  strong {
    color: #e2e8f0;
  }
`;

const Section = styled.div`
  background: rgba(30, 41, 59, 0.4);
  border: 1px solid rgba(71, 85, 105, 0.3);
  border-radius: 16px;
  margin-bottom: 12px;
  overflow: hidden;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  
  ${props => props.$isOpen && `
    border-color: rgba(59, 130, 246, 0.4);
    box-shadow: 0 0 30px rgba(59, 130, 246, 0.1);
  `}
`;

const SectionHeader = styled.div`
  padding: 18px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  background: ${props => props.$isOpen 
    ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(30, 41, 59, 0.6) 100%)'
    : 'rgba(30, 41, 59, 0.6)'};
  transition: all 0.3s ease;
  
  &:hover {
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(30, 41, 59, 0.7) 100%);
  }
`;

const SectionTitle = styled.span`
  font-size: 15px;
  font-weight: 500;
  color: ${props => props.$isOpen ? '#e2e8f0' : '#94a3b8'};
  transition: color 0.3s ease;
`;

const SectionContent = styled.div`
  padding: 24px;
  background: rgba(15, 23, 42, 0.5);
  border-top: 1px solid rgba(71, 85, 105, 0.2);
  animation: ${fadeIn} 0.3s ease-out;
`;

const FormGroup = styled.div`
  margin-bottom: 24px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #94a3b8;
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const OptionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const OptionItemStyled = styled.label`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: rgba(30, 41, 59, 0.5);
  border: 1px solid rgba(71, 85, 105, 0.3);
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(59, 130, 246, 0.1);
    border-color: rgba(59, 130, 246, 0.3);
    transform: translateX(4px);
  }
  
  ${props => props.$checked && `
    background: rgba(59, 130, 246, 0.15);
    border-color: rgba(59, 130, 246, 0.4);
  `}
`;

const CheckboxStyled = styled.input`
  width: 18px;
  height: 18px;
  min-width: 18px;
  margin-right: 16px;
  accent-color: #3b82f6;
  cursor: pointer;
  border-radius: 4px;
  flex-shrink: 0;
`;

const OptionText = styled.span`
  font-size: 14px;
  color: rgba(226, 232, 240, 0.9);
`;

const TextAreaStyled = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 14px 16px;
  background: rgba(15, 23, 42, 0.6);
  border: 1px solid rgba(71, 85, 105, 0.3);
  border-radius: 10px;
  color: #e2e8f0;
  font-size: 14px;
  resize: vertical;
  font-family: inherit;
  box-sizing: border-box;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: rgba(59, 130, 246, 0.5);
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
  
  &::placeholder {
    color: rgba(148, 163, 184, 0.5);
  }
`;

const InputStyled = styled.input`
  width: 100%;
  padding: 12px 16px;
  background: rgba(15, 23, 42, 0.6);
  border: 1px solid rgba(71, 85, 105, 0.3);
  border-radius: 10px;
  color: #e2e8f0;
  font-size: 14px;
  font-family: inherit;
  box-sizing: border-box;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: rgba(59, 130, 246, 0.5);
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
  
  &::placeholder {
    color: rgba(148, 163, 184, 0.5);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid rgba(71, 85, 105, 0.2);
`;

const ButtonSecondary = styled.button`
  padding: 12px 24px;
  background: rgba(71, 85, 105, 0.3);
  border: 1px solid rgba(71, 85, 105, 0.4);
  border-radius: 10px;
  color: #94a3b8;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(71, 85, 105, 0.5);
    color: #e2e8f0;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ButtonPrimary = styled.button`
  padding: 12px 24px;
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  border: none;
  border-radius: 10px;
  color: #FFFFFF;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const CustomRuleCard = styled.div`
  background: rgba(30, 41, 59, 0.5);
  border: 1px solid rgba(71, 85, 105, 0.3);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
`;

const DeleteButton = styled.button`
  padding: 8px 14px;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 8px;
  color: #f87171;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(239, 68, 68, 0.2);
  }
`;

const AddButton = styled.button`
  width: 100%;
  padding: 14px;
  background: transparent;
  border: 1px dashed rgba(59, 130, 246, 0.4);
  border-radius: 10px;
  color: #3b82f6;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(59, 130, 246, 0.1);
    border-color: rgba(59, 130, 246, 0.6);
  }
`;

const StatusMessage = styled.div`
  padding: 14px 18px;
  border-radius: 10px;
  margin-bottom: 20px;
  font-size: 14px;
  font-weight: 500;
  animation: ${fadeIn} 0.3s ease-out;
  
  ${props => props.$success && `
    background: rgba(34, 197, 94, 0.1);
    border: 1px solid rgba(34, 197, 94, 0.3);
    color: #4ade80;
  `}
  
  ${props => !props.$success && `
    background: rgba(239, 68, 68, 0.1);
    border: 1px solid rgba(239, 68, 68, 0.3);
    color: #f87171;
  `}
`;

// Legacy styles object for components that still need it
const styles = {
  input: {},
  textArea: {},
  addButton: {},
  deleteButton: {},
  customRuleCard: {}
};

// Initial form data
const initialFormState = {
  serviceTypes: {
    temporaryEntry: false,
    workAuthorization: false,
    studyAuthorization: false,
    permanentResidence: false,
    familyBased: false,
    extensions: false,
    statusChange: false,
    appeals: false
  },
  outsideScopeHandling: 'block',
  riskPosture: 'balanced',
  evidencePhilosophy: {
    minimal: false,
    overDocument: false,
    proactiveExplanations: false
  },
  seniorReviewPolicy: 'highRisk',
  identityDocumentsRequired: {
    passport: false,
    nationalId: false,
    birthCertificate: false,
    nameChange: false
  },
  identityMismatchHandling: 'require',
  mandatoryDisclosure: {
    priorRefusals: false,
    priorOverstays: false,
    priorRemovals: false,
    priorViolations: false,
    otherCountryApplications: false
  },
  priorIssuesHandling: 'requireExplanation',
  priorIssuesIgnoreYears: '',
  purposeMustBe: {
    clearlyStated: false,
    documentSupported: false,
    consistent: false
  },
  unclearIntentHandling: 'require',
  activityTypes: {
    employment: false,
    selfEmployment: false,
    study: false,
    internship: false,
    unemployed: false
  },
  activityEvidenceRequired: {
    offerLetter: false,
    employmentLetter: false,
    contract: false,
    enrollmentProof: false,
    activityExplanation: false
  },
  selfEmploymentHandling: {
    seniorReview: false,
    financialProof: false,
    businessRegistration: false,
    treatAsHighRisk: false
  },
  educationEvidenceRequired: {
    diplomas: false,
    transcripts: false,
    credentialEvaluation: false
  },
  educationMismatchHandling: 'require',
  proofOfFundsRequired: 'always',
  financialHistoryDuration: '6months',
  unusualTransactionsHandling: 'require',
  tiesEvidenceRequired: {
    employmentContinuity: false,
    studyContinuity: false,
    familyTies: false,
    property: false,
    longTermPlans: false
  },
  weakTiesHandling: 'require',
  gapsRequireExplanation: '3months',
  overlappingActivitiesHandling: 'explain',
  inconsistenciesHandling: 'block',
  dependentsIncluded: {
    spouse: false,
    children: false,
    parents: false
  },
  dependentsDocumentationPolicy: 'upfront',
  familyStatusChanges: 'report',
  documentStandards: {
    legibleScans: false,
    colorCopies: false,
    originalsWhenPossible: false
  },
  translationsPolicy: 'certified',
  nameVariationsHandling: 'explain',
  autoHighRiskIndicators: {
    priorRefusals: false,
    statusViolations: false,
    selfEmployment: false,
    frequentTravel: false,
    weakFinancials: false,
    inconsistentIntent: false
  },
  highRiskHandling: {
    seniorReview: false,
    extraDocumentation: false,
    writtenJustification: false,
    allowWithoutRestriction: false
  },
  preSubmissionChecklist: {
    allDocumentsUploaded: false,
    allExplanationsComplete: false,
    noInconsistencies: false,
    riskReviewComplete: false,
    seniorReviewComplete: false
  },
  checklistFailureHandling: 'block',
  statusMonitoring: 'hybrid',
  expiryRenewalTracking: true,
  futureEligibilityTriggers: 'both',
  communicationTone: 'neutral',
  riskDisclosure: 'full',
  automationLevel: 'hybrid',
  overridePermissions: 'senior',
  overrideLogging: 'reason',
  firmPhilosophy: '',
  edgeCaseNotes: ''
};

// Reusable Option component
const OptionItem = ({ type, checked, onChange, label }) => {
  return (
    <OptionItemStyled $checked={checked}>
      <CheckboxStyled
        type={type}
        checked={checked}
        onChange={onChange}
      />
      <OptionText>{label}</OptionText>
    </OptionItemStyled>
  );
};

// Chevron Icon component
const ChevronIcon = ({ isOpen }) => (
  <svg 
    width="20" 
    height="20" 
    viewBox="0 0 24 24" 
    fill="none" 
    style={{
      transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)'
    }}
  >
    <path 
      d="M6 9L12 15L18 9" 
      stroke={isOpen ? '#3b82f6' : 'rgba(148, 163, 184, 0.6)'} 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </svg>
);

// Collapsible Section component
const CollapsibleSection = ({ title, isOpen, onToggle, children }) => {
  return (
    <Section $isOpen={isOpen}>
      <SectionHeader $isOpen={isOpen} onClick={onToggle}>
        <SectionTitle $isOpen={isOpen}>{title}</SectionTitle>
        <ChevronIcon isOpen={isOpen} />
      </SectionHeader>
      {isOpen && (
        <SectionContent>
          {children}
        </SectionContent>
      )}
    </Section>
  );
};

const FirmStandards = () => {
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  
  const [formData, setFormData] = useState(initialFormState);
  const [openSections, setOpenSections] = useState({});
  // Custom rules attached to existing sections (keyed by sectionId)
  const [sectionCustomRules, setSectionCustomRules] = useState({});
  // Custom sections created by user
  const [customSections, setCustomSections] = useState([]);
  const [saving, setSaving] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const axios = useAuthenticatedAxios();
  const { user } = useAuth0();

  useEffect(() => {
    loadFirmStandards();
    loadCompanyName();
  }, []);

  const loadCompanyName = async () => {
    try {
      const response = await axios.get('/profile');
      if (response.data?.profile?.name) {
        setCompanyName(response.data.profile.name);
      }
    } catch (error) {
      console.error('Error loading company name:', error);
    }
  };

  const loadFirmStandards = async () => {
    try {
      const response = await axios.get('/settings/firm-standards');
      if (response.data && response.data.firmStandards) {
        const { sectionCustomRules: loadedSectionRules, customSections: loadedCustomSections, ...otherData } = response.data.firmStandards;
        setFormData({ ...initialFormState, ...otherData });
        if (loadedSectionRules && typeof loadedSectionRules === 'object') {
          setSectionCustomRules(loadedSectionRules);
        }
        if (loadedCustomSections && Array.isArray(loadedCustomSections)) {
          setCustomSections(loadedCustomSections);
        }
      }
    } catch (error) {
      console.error('Error loading firm standards:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setStatusMessage(null);
    
    try {
      const dataToSave = { ...formData, sectionCustomRules, customSections };
      await axios.post('/settings/firm-standards', { firmStandards: dataToSave });
      setStatusMessage({ success: true, text: 'Firm standards saved successfully!' });
      setTimeout(() => setStatusMessage(null), 3000);
    } catch (error) {
      console.error('Error saving firm standards:', error);
      setStatusMessage({ success: false, text: 'Failed to save firm standards. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    const confirmed = await confirm({
      title: 'Reset to Defaults',
      message: 'Are you sure you want to reset all settings to default values?',
      confirmText: 'Reset',
      cancelText: 'Cancel',
      type: 'warning'
    });
    
    if (confirmed) {
      setFormData(initialFormState);
      setSectionCustomRules({});
      setCustomSections([]);
      showToast('Settings reset to defaults', 'success');
    }
  };

  const toggleSection = (id) => {
    setOpenSections(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const updateField = (path, value) => {
    setFormData(prev => {
      const newData = { ...prev };
      const keys = path.split('.');
      let current = newData;
      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  const updateCheckbox = (path, field, value) => {
    setFormData(prev => {
      const newData = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let current = newData;
      for (const key of keys) {
        current = current[key];
      }
      current[field] = value;
      return newData;
    });
  };

  // Add a custom rule to an existing section
  const addRuleToSection = (sectionId) => {
    setSectionCustomRules(prev => ({
      ...prev,
      [sectionId]: [...(prev[sectionId] || []), { id: Date.now(), title: '', description: '' }]
    }));
  };

  // Update a custom rule in an existing section
  const updateSectionRule = (sectionId, ruleId, field, value) => {
    setSectionCustomRules(prev => ({
      ...prev,
      [sectionId]: (prev[sectionId] || []).map(rule => 
        rule.id === ruleId ? { ...rule, [field]: value } : rule
      )
    }));
  };

  // Delete a custom rule from an existing section
  const deleteSectionRule = (sectionId, ruleId) => {
    setSectionCustomRules(prev => ({
      ...prev,
      [sectionId]: (prev[sectionId] || []).filter(rule => rule.id !== ruleId)
    }));
  };

  // Add a new custom section
  const addCustomSection = () => {
    setCustomSections(prev => [...prev, { 
      id: Date.now(), 
      title: '', 
      rules: [] 
    }]);
  };

  // Update custom section title
  const updateCustomSectionTitle = (sectionId, title) => {
    setCustomSections(prev => prev.map(section => 
      section.id === sectionId ? { ...section, title } : section
    ));
  };

  // Delete a custom section
  const deleteCustomSection = (sectionId) => {
    setCustomSections(prev => prev.filter(section => section.id !== sectionId));
  };

  // Add a rule to a custom section
  const addRuleToCustomSection = (sectionId) => {
    setCustomSections(prev => prev.map(section => 
      section.id === sectionId 
        ? { ...section, rules: [...section.rules, { id: Date.now(), title: '', description: '' }] }
        : section
    ));
  };

  // Update a rule in a custom section
  const updateCustomSectionRule = (sectionId, ruleId, field, value) => {
    setCustomSections(prev => prev.map(section => 
      section.id === sectionId 
        ? { ...section, rules: section.rules.map(rule => 
            rule.id === ruleId ? { ...rule, [field]: value } : rule
          )}
        : section
    ));
  };

  // Delete a rule from a custom section
  const deleteCustomSectionRule = (sectionId, ruleId) => {
    setCustomSections(prev => prev.map(section => 
      section.id === sectionId 
        ? { ...section, rules: section.rules.filter(rule => rule.id !== ruleId) }
        : section
    ));
  };

  // Render custom rules for a section
  const renderSectionCustomRules = (sectionId) => {
    const rules = sectionCustomRules[sectionId] || [];
    return (
      <>
        {rules.length > 0 && (
          <div style={{ marginTop: '24px', paddingTop: '20px', borderTop: '1px dashed rgba(71, 85, 105, 0.3)' }}>
            <Label style={{ color: '#3b82f6' }}>Custom Rules</Label>
            {rules.map(rule => (
              <CustomRuleCard key={rule.id}>
                <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                  <InputStyled
                    type="text"
                    style={{ flex: 1 }}
                    placeholder="Rule Title"
                    value={rule.title}
                    onChange={(e) => updateSectionRule(sectionId, rule.id, 'title', e.target.value)}
                  />
                  <DeleteButton onClick={() => deleteSectionRule(sectionId, rule.id)}>
                    Delete
                  </DeleteButton>
                </div>
                <TextAreaStyled
                  placeholder="Describe this rule..."
                  value={rule.description}
                  onChange={(e) => updateSectionRule(sectionId, rule.id, 'description', e.target.value)}
                />
              </CustomRuleCard>
            ))}
          </div>
        )}
        <AddButton 
          style={{ marginTop: '16px' }} 
          onClick={() => addRuleToSection(sectionId)}
        >
          + Add Custom Rule to This Section
        </AddButton>
      </>
    );
  };

  return (
    <PageWrapper>
      <Container>
        {/* Header */}
        <Header>
          <Title>{companyName ? `${companyName} Firm Standards` : 'Firm Standards'}</Title>
          <Subtitle>
            Configure your firm's policies, risk posture, and operating procedures. 
            These standards apply universally across all immigration services.
          </Subtitle>
        </Header>

        {/* Info Box */}
        <InfoBox>
          <InfoIcon>i</InfoIcon>
          <InfoText>
            <strong>How it works:</strong> Click on any section to expand it. 
            Configure your firm's standards and click "Save" at the bottom.
          </InfoText>
        </InfoBox>

        {/* Status Message */}
        {statusMessage && (
          <StatusMessage $success={statusMessage.success}>
            {statusMessage.text}
          </StatusMessage>
        )}

        {/* Section 1 */}
        <CollapsibleSection 
          title="Case Type & Service Scope"
          isOpen={openSections[1]}
          onToggle={() => toggleSection(1)}
        >
          <FormGroup>
            <Label>Service Types Handled by Firm</Label>
            <OptionsList>
              <OptionItem type="checkbox" checked={formData.serviceTypes.temporaryEntry} onChange={(e) => updateCheckbox('serviceTypes', 'temporaryEntry', e.target.checked)} label="Temporary entry (visitor / tourist)" />
              <OptionItem type="checkbox" checked={formData.serviceTypes.workAuthorization} onChange={(e) => updateCheckbox('serviceTypes', 'workAuthorization', e.target.checked)} label="Work authorization / work permit" />
              <OptionItem type="checkbox" checked={formData.serviceTypes.studyAuthorization} onChange={(e) => updateCheckbox('serviceTypes', 'studyAuthorization', e.target.checked)} label="Study authorization / study permit" />
              <OptionItem type="checkbox" checked={formData.serviceTypes.permanentResidence} onChange={(e) => updateCheckbox('serviceTypes', 'permanentResidence', e.target.checked)} label="Permanent residence" />
              <OptionItem type="checkbox" checked={formData.serviceTypes.familyBased} onChange={(e) => updateCheckbox('serviceTypes', 'familyBased', e.target.checked)} label="Family-based immigration" />
              <OptionItem type="checkbox" checked={formData.serviceTypes.extensions} onChange={(e) => updateCheckbox('serviceTypes', 'extensions', e.target.checked)} label="Extensions / renewals" />
              <OptionItem type="checkbox" checked={formData.serviceTypes.statusChange} onChange={(e) => updateCheckbox('serviceTypes', 'statusChange', e.target.checked)} label="Status change inside country" />
              <OptionItem type="checkbox" checked={formData.serviceTypes.appeals} onChange={(e) => updateCheckbox('serviceTypes', 'appeals', e.target.checked)} label="Appeals / reconsiderations" />
            </OptionsList>
          </FormGroup>
          <FormGroup>
            <Label>If service type is outside firm scope</Label>
            <OptionsList>
              <OptionItem type="radio" checked={formData.outsideScopeHandling === 'block'} onChange={() => updateField('outsideScopeHandling', 'block')} label="Block intake" />
              <OptionItem type="radio" checked={formData.outsideScopeHandling === 'flag'} onChange={() => updateField('outsideScopeHandling', 'flag')} label="Allow intake but flag" />
              <OptionItem type="radio" checked={formData.outsideScopeHandling === 'allow'} onChange={() => updateField('outsideScopeHandling', 'allow')} label="Allow intake freely" />
            </OptionsList>
          </FormGroup>
          {renderSectionCustomRules(1)}
        </CollapsibleSection>

        {/* Section 2 */}
        <CollapsibleSection 
          title="General Filing Philosophy"
          isOpen={openSections[2]}
          onToggle={() => toggleSection(2)}
        >
          <FormGroup>
            <Label>Risk Posture</Label>
            <OptionsList>
              <OptionItem type="radio" checked={formData.riskPosture === 'conservative'} onChange={() => updateField('riskPosture', 'conservative')} label="Conservative (avoid borderline cases)" />
              <OptionItem type="radio" checked={formData.riskPosture === 'balanced'} onChange={() => updateField('riskPosture', 'balanced')} label="Balanced" />
              <OptionItem type="radio" checked={formData.riskPosture === 'aggressive'} onChange={() => updateField('riskPosture', 'aggressive')} label="Aggressive" />
            </OptionsList>
          </FormGroup>
          <FormGroup>
            <Label>Evidence Philosophy</Label>
            <OptionsList>
              <OptionItem type="checkbox" checked={formData.evidencePhilosophy.minimal} onChange={(e) => updateCheckbox('evidencePhilosophy', 'minimal', e.target.checked)} label="Minimal required only" />
              <OptionItem type="checkbox" checked={formData.evidencePhilosophy.overDocument} onChange={(e) => updateCheckbox('evidencePhilosophy', 'overDocument', e.target.checked)} label="Over-document when ambiguity exists" />
              <OptionItem type="checkbox" checked={formData.evidencePhilosophy.proactiveExplanations} onChange={(e) => updateCheckbox('evidencePhilosophy', 'proactiveExplanations', e.target.checked)} label="Always include proactive explanations" />
            </OptionsList>
          </FormGroup>
          <FormGroup>
            <Label>Senior Review Policy</Label>
            <OptionsList>
              <OptionItem type="radio" checked={formData.seniorReviewPolicy === 'all'} onChange={() => updateField('seniorReviewPolicy', 'all')} label="All cases require senior review" />
              <OptionItem type="radio" checked={formData.seniorReviewPolicy === 'highRisk'} onChange={() => updateField('seniorReviewPolicy', 'highRisk')} label="Only high-risk cases" />
              <OptionItem type="radio" checked={formData.seniorReviewPolicy === 'specificTypes'} onChange={() => updateField('seniorReviewPolicy', 'specificTypes')} label="Only specific service types" />
              <OptionItem type="radio" checked={formData.seniorReviewPolicy === 'none'} onChange={() => updateField('seniorReviewPolicy', 'none')} label="No mandatory senior review" />
            </OptionsList>
          </FormGroup>
          {renderSectionCustomRules(2)}
        </CollapsibleSection>

        {/* Section 3 */}
        <CollapsibleSection 
          title="Identity & Personal History"
          isOpen={openSections[3]}
          onToggle={() => toggleSection(3)}
        >
          <FormGroup>
            <Label>Identity Documents Required</Label>
            <OptionsList>
              <OptionItem type="checkbox" checked={formData.identityDocumentsRequired.passport} onChange={(e) => updateCheckbox('identityDocumentsRequired', 'passport', e.target.checked)} label="Passport" />
              <OptionItem type="checkbox" checked={formData.identityDocumentsRequired.nationalId} onChange={(e) => updateCheckbox('identityDocumentsRequired', 'nationalId', e.target.checked)} label="National ID (if applicable)" />
              <OptionItem type="checkbox" checked={formData.identityDocumentsRequired.birthCertificate} onChange={(e) => updateCheckbox('identityDocumentsRequired', 'birthCertificate', e.target.checked)} label="Birth certificate" />
              <OptionItem type="checkbox" checked={formData.identityDocumentsRequired.nameChange} onChange={(e) => updateCheckbox('identityDocumentsRequired', 'nameChange', e.target.checked)} label="Name change documents (if applicable)" />
            </OptionsList>
          </FormGroup>
          <FormGroup>
            <Label>If identity mismatch detected</Label>
            <OptionsList>
              <OptionItem type="radio" checked={formData.identityMismatchHandling === 'block'} onChange={() => updateField('identityMismatchHandling', 'block')} label="Block progression" />
              <OptionItem type="radio" checked={formData.identityMismatchHandling === 'require'} onChange={() => updateField('identityMismatchHandling', 'require')} label="Require explanation" />
              <OptionItem type="radio" checked={formData.identityMismatchHandling === 'flag'} onChange={() => updateField('identityMismatchHandling', 'flag')} label="Flag only" />
            </OptionsList>
          </FormGroup>
          {renderSectionCustomRules(3)}
        </CollapsibleSection>

        {/* Section 4 */}
        <CollapsibleSection 
          title="Immigration History"
          isOpen={openSections[4]}
          onToggle={() => toggleSection(4)}
        >
          <FormGroup>
            <Label>Mandatory Disclosure</Label>
            <OptionsList>
              <OptionItem type="checkbox" checked={formData.mandatoryDisclosure.priorRefusals} onChange={(e) => updateCheckbox('mandatoryDisclosure', 'priorRefusals', e.target.checked)} label="Prior refusals / denials" />
              <OptionItem type="checkbox" checked={formData.mandatoryDisclosure.priorOverstays} onChange={(e) => updateCheckbox('mandatoryDisclosure', 'priorOverstays', e.target.checked)} label="Prior overstays" />
              <OptionItem type="checkbox" checked={formData.mandatoryDisclosure.priorRemovals} onChange={(e) => updateCheckbox('mandatoryDisclosure', 'priorRemovals', e.target.checked)} label="Prior removals / deportations" />
              <OptionItem type="checkbox" checked={formData.mandatoryDisclosure.priorViolations} onChange={(e) => updateCheckbox('mandatoryDisclosure', 'priorViolations', e.target.checked)} label="Prior status violations" />
              <OptionItem type="checkbox" checked={formData.mandatoryDisclosure.otherCountryApplications} onChange={(e) => updateCheckbox('mandatoryDisclosure', 'otherCountryApplications', e.target.checked)} label="Applications in other countries" />
            </OptionsList>
          </FormGroup>
          <FormGroup>
            <Label>Handling prior issues</Label>
            <OptionsList>
              <OptionItem type="radio" checked={formData.priorIssuesHandling === 'requireExplanation'} onChange={() => updateField('priorIssuesHandling', 'requireExplanation')} label="Always require explanation" />
              <OptionItem type="radio" checked={formData.priorIssuesHandling === 'sameCountryOnly'} onChange={() => updateField('priorIssuesHandling', 'sameCountryOnly')} label="Require explanation if same country" />
              <OptionItem type="radio" checked={formData.priorIssuesHandling === 'flag'} onChange={() => updateField('priorIssuesHandling', 'flag')} label="Flag only" />
              <OptionItem type="radio" checked={formData.priorIssuesHandling === 'ignore'} onChange={() => updateField('priorIssuesHandling', 'ignore')} label="Ignore if older than specified years" />
            </OptionsList>
          </FormGroup>
          {renderSectionCustomRules(4)}
        </CollapsibleSection>

        {/* Section 5 */}
        <CollapsibleSection 
          title="Purpose & Intent Validation"
          isOpen={openSections[5]}
          onToggle={() => toggleSection(5)}
        >
          <FormGroup>
            <Label>Purpose of Stay Must Be</Label>
            <OptionsList>
              <OptionItem type="checkbox" checked={formData.purposeMustBe.clearlyStated} onChange={(e) => updateCheckbox('purposeMustBe', 'clearlyStated', e.target.checked)} label="Clearly stated" />
              <OptionItem type="checkbox" checked={formData.purposeMustBe.documentSupported} onChange={(e) => updateCheckbox('purposeMustBe', 'documentSupported', e.target.checked)} label="Supported by documents" />
              <OptionItem type="checkbox" checked={formData.purposeMustBe.consistent} onChange={(e) => updateCheckbox('purposeMustBe', 'consistent', e.target.checked)} label="Consistent across all materials" />
            </OptionsList>
          </FormGroup>
          <FormGroup>
            <Label>If intent is unclear or mixed</Label>
            <OptionsList>
              <OptionItem type="radio" checked={formData.unclearIntentHandling === 'require'} onChange={() => updateField('unclearIntentHandling', 'require')} label="Require explanation letter" />
              <OptionItem type="radio" checked={formData.unclearIntentHandling === 'flag'} onChange={() => updateField('unclearIntentHandling', 'flag')} label="Flag as high-risk" />
              <OptionItem type="radio" checked={formData.unclearIntentHandling === 'block'} onChange={() => updateField('unclearIntentHandling', 'block')} label="Block filing until resolved" />
            </OptionsList>
          </FormGroup>
          {renderSectionCustomRules(5)}
        </CollapsibleSection>

        {/* Section 6 */}
        <CollapsibleSection 
          title="Employment / Activity Evidence"
          isOpen={openSections[6]}
          onToggle={() => toggleSection(6)}
        >
          <FormGroup>
            <Label>Activity Types</Label>
            <OptionsList>
              <OptionItem type="checkbox" checked={formData.activityTypes.employment} onChange={(e) => updateCheckbox('activityTypes', 'employment', e.target.checked)} label="Employment" />
              <OptionItem type="checkbox" checked={formData.activityTypes.selfEmployment} onChange={(e) => updateCheckbox('activityTypes', 'selfEmployment', e.target.checked)} label="Self-employment" />
              <OptionItem type="checkbox" checked={formData.activityTypes.study} onChange={(e) => updateCheckbox('activityTypes', 'study', e.target.checked)} label="Study" />
              <OptionItem type="checkbox" checked={formData.activityTypes.internship} onChange={(e) => updateCheckbox('activityTypes', 'internship', e.target.checked)} label="Internship / training" />
              <OptionItem type="checkbox" checked={formData.activityTypes.unemployed} onChange={(e) => updateCheckbox('activityTypes', 'unemployed', e.target.checked)} label="Unemployed / gap" />
            </OptionsList>
          </FormGroup>
          <FormGroup>
            <Label>Required Evidence</Label>
            <OptionsList>
              <OptionItem type="checkbox" checked={formData.activityEvidenceRequired.offerLetter} onChange={(e) => updateCheckbox('activityEvidenceRequired', 'offerLetter', e.target.checked)} label="Offer / admission letter" />
              <OptionItem type="checkbox" checked={formData.activityEvidenceRequired.employmentLetter} onChange={(e) => updateCheckbox('activityEvidenceRequired', 'employmentLetter', e.target.checked)} label="Employment letter" />
              <OptionItem type="checkbox" checked={formData.activityEvidenceRequired.contract} onChange={(e) => updateCheckbox('activityEvidenceRequired', 'contract', e.target.checked)} label="Contract" />
              <OptionItem type="checkbox" checked={formData.activityEvidenceRequired.enrollmentProof} onChange={(e) => updateCheckbox('activityEvidenceRequired', 'enrollmentProof', e.target.checked)} label="Proof of enrollment" />
              <OptionItem type="checkbox" checked={formData.activityEvidenceRequired.activityExplanation} onChange={(e) => updateCheckbox('activityEvidenceRequired', 'activityExplanation', e.target.checked)} label="Activity explanation" />
            </OptionsList>
          </FormGroup>
          {renderSectionCustomRules(6)}
        </CollapsibleSection>

        {/* Section 7 */}
        <CollapsibleSection 
          title="Education & Qualifications"
          isOpen={openSections[7]}
          onToggle={() => toggleSection(7)}
        >
          <FormGroup>
            <Label>Education Evidence Required</Label>
            <OptionsList>
              <OptionItem type="checkbox" checked={formData.educationEvidenceRequired.diplomas} onChange={(e) => updateCheckbox('educationEvidenceRequired', 'diplomas', e.target.checked)} label="Diplomas" />
              <OptionItem type="checkbox" checked={formData.educationEvidenceRequired.transcripts} onChange={(e) => updateCheckbox('educationEvidenceRequired', 'transcripts', e.target.checked)} label="Transcripts" />
              <OptionItem type="checkbox" checked={formData.educationEvidenceRequired.credentialEvaluation} onChange={(e) => updateCheckbox('educationEvidenceRequired', 'credentialEvaluation', e.target.checked)} label="Credential evaluation (if relevant)" />
            </OptionsList>
          </FormGroup>
          <FormGroup>
            <Label>Mismatch handling</Label>
            <OptionsList>
              <OptionItem type="radio" checked={formData.educationMismatchHandling === 'require'} onChange={() => updateField('educationMismatchHandling', 'require')} label="Require explanation" />
              <OptionItem type="radio" checked={formData.educationMismatchHandling === 'additional'} onChange={() => updateField('educationMismatchHandling', 'additional')} label="Require additional experience proof" />
              <OptionItem type="radio" checked={formData.educationMismatchHandling === 'flag'} onChange={() => updateField('educationMismatchHandling', 'flag')} label="Flag as risk" />
              <OptionItem type="radio" checked={formData.educationMismatchHandling === 'allow'} onChange={() => updateField('educationMismatchHandling', 'allow')} label="Allow without action" />
            </OptionsList>
          </FormGroup>
          {renderSectionCustomRules(7)}
        </CollapsibleSection>

        {/* Section 8 */}
        <CollapsibleSection 
          title="Financial Capacity"
          isOpen={openSections[8]}
          onToggle={() => toggleSection(8)}
        >
          <FormGroup>
            <Label>Proof of Funds Required</Label>
            <OptionsList>
              <OptionItem type="radio" checked={formData.proofOfFundsRequired === 'always'} onChange={() => updateField('proofOfFundsRequired', 'always')} label="Always" />
              <OptionItem type="radio" checked={formData.proofOfFundsRequired === 'whenRequired'} onChange={() => updateField('proofOfFundsRequired', 'whenRequired')} label="Only when required by program" />
              <OptionItem type="radio" checked={formData.proofOfFundsRequired === 'ifRisk'} onChange={() => updateField('proofOfFundsRequired', 'ifRisk')} label="Only if risk flagged" />
              <OptionItem type="radio" checked={formData.proofOfFundsRequired === 'never'} onChange={() => updateField('proofOfFundsRequired', 'never')} label="Never" />
            </OptionsList>
          </FormGroup>
          <FormGroup>
            <Label>Financial History Duration</Label>
            <OptionsList>
              <OptionItem type="radio" checked={formData.financialHistoryDuration === '3months'} onChange={() => updateField('financialHistoryDuration', '3months')} label="3 months" />
              <OptionItem type="radio" checked={formData.financialHistoryDuration === '6months'} onChange={() => updateField('financialHistoryDuration', '6months')} label="6 months" />
              <OptionItem type="radio" checked={formData.financialHistoryDuration === '12months'} onChange={() => updateField('financialHistoryDuration', '12months')} label="12 months" />
            </OptionsList>
          </FormGroup>
          {renderSectionCustomRules(8)}
        </CollapsibleSection>

        {/* Section 9-18: Simplified for brevity */}
        <CollapsibleSection title="Ties, Continuity & Future Plans" isOpen={openSections[9]} onToggle={() => toggleSection(9)}>
          <FormGroup>
            <Label>Ties / Continuity Evidence</Label>
            <OptionsList>
              <OptionItem type="checkbox" checked={formData.tiesEvidenceRequired.employmentContinuity} onChange={(e) => updateCheckbox('tiesEvidenceRequired', 'employmentContinuity', e.target.checked)} label="Employment continuity" />
              <OptionItem type="checkbox" checked={formData.tiesEvidenceRequired.studyContinuity} onChange={(e) => updateCheckbox('tiesEvidenceRequired', 'studyContinuity', e.target.checked)} label="Study continuity" />
              <OptionItem type="checkbox" checked={formData.tiesEvidenceRequired.familyTies} onChange={(e) => updateCheckbox('tiesEvidenceRequired', 'familyTies', e.target.checked)} label="Family ties" />
              <OptionItem type="checkbox" checked={formData.tiesEvidenceRequired.property} onChange={(e) => updateCheckbox('tiesEvidenceRequired', 'property', e.target.checked)} label="Property / residence" />
              <OptionItem type="checkbox" checked={formData.tiesEvidenceRequired.longTermPlans} onChange={(e) => updateCheckbox('tiesEvidenceRequired', 'longTermPlans', e.target.checked)} label="Long-term plans explanation" />
            </OptionsList>
          </FormGroup>
          {renderSectionCustomRules(9)}
        </CollapsibleSection>

        <CollapsibleSection title="Timeline, Gaps & Consistency" isOpen={openSections[10]} onToggle={() => toggleSection(10)}>
          <FormGroup>
            <Label>Gaps Require Explanation</Label>
            <OptionsList>
              <OptionItem type="radio" checked={formData.gapsRequireExplanation === '1month'} onChange={() => updateField('gapsRequireExplanation', '1month')} label="1 month" />
              <OptionItem type="radio" checked={formData.gapsRequireExplanation === '3months'} onChange={() => updateField('gapsRequireExplanation', '3months')} label="3 months" />
              <OptionItem type="radio" checked={formData.gapsRequireExplanation === '6months'} onChange={() => updateField('gapsRequireExplanation', '6months')} label="6 months" />
              <OptionItem type="radio" checked={formData.gapsRequireExplanation === '12months'} onChange={() => updateField('gapsRequireExplanation', '12months')} label="12 months" />
            </OptionsList>
          </FormGroup>
          {renderSectionCustomRules(10)}
        </CollapsibleSection>

        <CollapsibleSection title="Dependents & Family" isOpen={openSections[11]} onToggle={() => toggleSection(11)}>
          <FormGroup>
            <Label>Dependents Included</Label>
            <OptionsList>
              <OptionItem type="checkbox" checked={formData.dependentsIncluded.spouse} onChange={(e) => updateCheckbox('dependentsIncluded', 'spouse', e.target.checked)} label="Spouse / partner" />
              <OptionItem type="checkbox" checked={formData.dependentsIncluded.children} onChange={(e) => updateCheckbox('dependentsIncluded', 'children', e.target.checked)} label="Children" />
              <OptionItem type="checkbox" checked={formData.dependentsIncluded.parents} onChange={(e) => updateCheckbox('dependentsIncluded', 'parents', e.target.checked)} label="Parents" />
            </OptionsList>
          </FormGroup>
          {renderSectionCustomRules(11)}
        </CollapsibleSection>

        <CollapsibleSection title="Document Quality & Translation" isOpen={openSections[12]} onToggle={() => toggleSection(12)}>
          <FormGroup>
            <Label>Document Standards</Label>
            <OptionsList>
              <OptionItem type="checkbox" checked={formData.documentStandards.legibleScans} onChange={(e) => updateCheckbox('documentStandards', 'legibleScans', e.target.checked)} label="Legible scans" />
              <OptionItem type="checkbox" checked={formData.documentStandards.colorCopies} onChange={(e) => updateCheckbox('documentStandards', 'colorCopies', e.target.checked)} label="Color copies" />
              <OptionItem type="checkbox" checked={formData.documentStandards.originalsWhenPossible} onChange={(e) => updateCheckbox('documentStandards', 'originalsWhenPossible', e.target.checked)} label="Originals when possible" />
            </OptionsList>
          </FormGroup>
          <FormGroup>
            <Label>Translations</Label>
            <OptionsList>
              <OptionItem type="radio" checked={formData.translationsPolicy === 'certified'} onChange={() => updateField('translationsPolicy', 'certified')} label="Certified translations required" />
              <OptionItem type="radio" checked={formData.translationsPolicy === 'nonCertified'} onChange={() => updateField('translationsPolicy', 'nonCertified')} label="Non-certified allowed" />
              <OptionItem type="radio" checked={formData.translationsPolicy === 'affidavit'} onChange={() => updateField('translationsPolicy', 'affidavit')} label="Affidavit required" />
            </OptionsList>
          </FormGroup>
          {renderSectionCustomRules(12)}
        </CollapsibleSection>

        <CollapsibleSection title="Risk Sensitivity & Escalation" isOpen={openSections[13]} onToggle={() => toggleSection(13)}>
          <FormGroup>
            <Label>Automatically High-Risk Indicators</Label>
            <OptionsList>
              <OptionItem type="checkbox" checked={formData.autoHighRiskIndicators.priorRefusals} onChange={(e) => updateCheckbox('autoHighRiskIndicators', 'priorRefusals', e.target.checked)} label="Prior refusals" />
              <OptionItem type="checkbox" checked={formData.autoHighRiskIndicators.statusViolations} onChange={(e) => updateCheckbox('autoHighRiskIndicators', 'statusViolations', e.target.checked)} label="Status violations" />
              <OptionItem type="checkbox" checked={formData.autoHighRiskIndicators.selfEmployment} onChange={(e) => updateCheckbox('autoHighRiskIndicators', 'selfEmployment', e.target.checked)} label="Self-employment" />
              <OptionItem type="checkbox" checked={formData.autoHighRiskIndicators.frequentTravel} onChange={(e) => updateCheckbox('autoHighRiskIndicators', 'frequentTravel', e.target.checked)} label="Frequent travel" />
              <OptionItem type="checkbox" checked={formData.autoHighRiskIndicators.weakFinancials} onChange={(e) => updateCheckbox('autoHighRiskIndicators', 'weakFinancials', e.target.checked)} label="Weak financials" />
              <OptionItem type="checkbox" checked={formData.autoHighRiskIndicators.inconsistentIntent} onChange={(e) => updateCheckbox('autoHighRiskIndicators', 'inconsistentIntent', e.target.checked)} label="Inconsistent intent" />
            </OptionsList>
          </FormGroup>
          {renderSectionCustomRules(13)}
        </CollapsibleSection>

        <CollapsibleSection title="Pre-Submission Readiness Gate" isOpen={openSections[14]} onToggle={() => toggleSection(14)}>
          <FormGroup>
            <Label>Before a case can move to "Ready to Submit"</Label>
            <OptionsList>
              <OptionItem type="checkbox" checked={formData.preSubmissionChecklist.allDocumentsUploaded} onChange={(e) => updateCheckbox('preSubmissionChecklist', 'allDocumentsUploaded', e.target.checked)} label="All required documents uploaded" />
              <OptionItem type="checkbox" checked={formData.preSubmissionChecklist.allExplanationsComplete} onChange={(e) => updateCheckbox('preSubmissionChecklist', 'allExplanationsComplete', e.target.checked)} label="All explanations completed" />
              <OptionItem type="checkbox" checked={formData.preSubmissionChecklist.noInconsistencies} onChange={(e) => updateCheckbox('preSubmissionChecklist', 'noInconsistencies', e.target.checked)} label="No unresolved inconsistencies" />
              <OptionItem type="checkbox" checked={formData.preSubmissionChecklist.riskReviewComplete} onChange={(e) => updateCheckbox('preSubmissionChecklist', 'riskReviewComplete', e.target.checked)} label="Risk review completed" />
              <OptionItem type="checkbox" checked={formData.preSubmissionChecklist.seniorReviewComplete} onChange={(e) => updateCheckbox('preSubmissionChecklist', 'seniorReviewComplete', e.target.checked)} label="Senior review completed (if required)" />
            </OptionsList>
          </FormGroup>
          {renderSectionCustomRules(14)}
        </CollapsibleSection>

        <CollapsibleSection title="Post-Submission & Lifecycle" isOpen={openSections[15]} onToggle={() => toggleSection(15)}>
          <FormGroup>
            <Label>Status Monitoring</Label>
            <OptionsList>
              <OptionItem type="radio" checked={formData.statusMonitoring === 'automated'} onChange={() => updateField('statusMonitoring', 'automated')} label="Automated updates allowed" />
              <OptionItem type="radio" checked={formData.statusMonitoring === 'manual'} onChange={() => updateField('statusMonitoring', 'manual')} label="Manual only" />
              <OptionItem type="radio" checked={formData.statusMonitoring === 'hybrid'} onChange={() => updateField('statusMonitoring', 'hybrid')} label="Hybrid" />
            </OptionsList>
          </FormGroup>
          {renderSectionCustomRules(15)}
        </CollapsibleSection>

        <CollapsibleSection title="Client Communication Standards" isOpen={openSections[16]} onToggle={() => toggleSection(16)}>
          <FormGroup>
            <Label>Tone</Label>
            <OptionsList>
              <OptionItem type="radio" checked={formData.communicationTone === 'formal'} onChange={() => updateField('communicationTone', 'formal')} label="Formal" />
              <OptionItem type="radio" checked={formData.communicationTone === 'neutral'} onChange={() => updateField('communicationTone', 'neutral')} label="Neutral" />
              <OptionItem type="radio" checked={formData.communicationTone === 'reassuring'} onChange={() => updateField('communicationTone', 'reassuring')} label="Reassuring" />
            </OptionsList>
          </FormGroup>
          <FormGroup>
            <Label>Risk Disclosure</Label>
            <OptionsList>
              <OptionItem type="radio" checked={formData.riskDisclosure === 'full'} onChange={() => updateField('riskDisclosure', 'full')} label="Full disclosure" />
              <OptionItem type="radio" checked={formData.riskDisclosure === 'highRisk'} onChange={() => updateField('riskDisclosure', 'highRisk')} label="High-risk only" />
              <OptionItem type="radio" checked={formData.riskDisclosure === 'minimal'} onChange={() => updateField('riskDisclosure', 'minimal')} label="Minimal disclosure" />
            </OptionsList>
          </FormGroup>
          {renderSectionCustomRules(16)}
        </CollapsibleSection>

        <CollapsibleSection title="Overrides, Accountability & Audit" isOpen={openSections[17]} onToggle={() => toggleSection(17)}>
          <FormGroup>
            <Label>Override Permissions</Label>
            <OptionsList>
              <OptionItem type="radio" checked={formData.overridePermissions === 'senior'} onChange={() => updateField('overridePermissions', 'senior')} label="Senior staff only" />
              <OptionItem type="radio" checked={formData.overridePermissions === 'anyStaff'} onChange={() => updateField('overridePermissions', 'anyStaff')} label="Any staff with reason" />
              <OptionItem type="radio" checked={formData.overridePermissions === 'disabled'} onChange={() => updateField('overridePermissions', 'disabled')} label="Overrides disabled" />
            </OptionsList>
          </FormGroup>
          <FormGroup>
            <Label>Override Logging</Label>
            <OptionsList>
              <OptionItem type="radio" checked={formData.overrideLogging === 'reason'} onChange={() => updateField('overrideLogging', 'reason')} label="Log reason required" />
              <OptionItem type="radio" checked={formData.overrideLogging === 'logOnly'} onChange={() => updateField('overrideLogging', 'logOnly')} label="Log only" />
              <OptionItem type="radio" checked={formData.overrideLogging === 'none'} onChange={() => updateField('overrideLogging', 'none')} label="No logging" />
            </OptionsList>
          </FormGroup>
          {renderSectionCustomRules(17)}
        </CollapsibleSection>

        <CollapsibleSection title="Internal Guidance" isOpen={openSections[18]} onToggle={() => toggleSection(18)}>
          <FormGroup>
            <Label>Firm Philosophy</Label>
            <TextAreaStyled
              value={formData.firmPhilosophy}
              onChange={(e) => updateField('firmPhilosophy', e.target.value)}
              placeholder="In our firm, we generally treat immigration cases as..."
            />
          </FormGroup>
          <FormGroup>
            <Label>Edge Case Notes</Label>
            <TextAreaStyled
              value={formData.edgeCaseNotes}
              onChange={(e) => updateField('edgeCaseNotes', e.target.value)}
              placeholder="When cases don't fit cleanly, we usually..."
            />
          </FormGroup>
          {renderSectionCustomRules(18)}
        </CollapsibleSection>

        {/* Custom Sections */}
        {customSections.map(section => (
          <CollapsibleSection 
            key={section.id}
            title={section.title || 'New Custom Section'} 
            isOpen={openSections[`custom-${section.id}`]} 
            onToggle={() => toggleSection(`custom-${section.id}`)}
          >
            <FormGroup>
              <Label>Section Name</Label>
              <InputStyled
                type="text"
                placeholder="Enter section name..."
                value={section.title}
                onChange={(e) => updateCustomSectionTitle(section.id, e.target.value)}
              />
            </FormGroup>
            
            <FormGroup>
              <Label>Rules in This Section</Label>
              {section.rules.map(rule => (
                <CustomRuleCard key={rule.id}>
                  <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                    <InputStyled
                      type="text"
                      style={{ flex: 1 }}
                      placeholder="Rule Title"
                      value={rule.title}
                      onChange={(e) => updateCustomSectionRule(section.id, rule.id, 'title', e.target.value)}
                    />
                    <DeleteButton onClick={() => deleteCustomSectionRule(section.id, rule.id)}>
                      Delete
                    </DeleteButton>
                  </div>
                  <TextAreaStyled
                    placeholder="Describe this rule..."
                    value={rule.description}
                    onChange={(e) => updateCustomSectionRule(section.id, rule.id, 'description', e.target.value)}
                  />
                </CustomRuleCard>
              ))}
              
              <AddButton 
                style={{ marginBottom: '16px' }} 
                onClick={() => addRuleToCustomSection(section.id)}
              >
                + Add Rule
              </AddButton>
            </FormGroup>
            
            <DeleteButton 
              style={{ width: '100%', padding: '12px' }} 
              onClick={() => deleteCustomSection(section.id)}
            >
              Delete This Section
            </DeleteButton>
          </CollapsibleSection>
        ))}

        {/* Add New Custom Section Button */}
        <AddButton 
          style={{ marginBottom: '20px' }} 
          onClick={addCustomSection}
        >
          + Add New Custom Section
        </AddButton>

        {/* Buttons */}
        <ButtonGroup>
          <ButtonSecondary onClick={handleReset} disabled={saving}>
            Reset to Defaults
          </ButtonSecondary>
          <ButtonPrimary onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Standards'}
          </ButtonPrimary>
        </ButtonGroup>
      </Container>
    </PageWrapper>
  );
};

export default FirmStandards;
